import { useCallback, useRef, useState } from 'react';
import { ConfirmCreateShareDialog } from '../components/ConfirmCreateShareDialog';

export type ConfirmDialogResolver = (result: boolean) => void;
export type ConfirmDialogResultCallback = () => Promise<boolean>;

/**
 * Hook that provides a confirmation dialog for creating shares.
 *
 * This hook manages the state and logic for showing a confirmation dialog
 * when creating a share, and returns both the dialog component and a method
 * to trigger the confirmation flow.
 *
 * @returns An object containing:
 *   - confirmCreateShareDialog: The dialog component to be rendered
 *   - getConfirmCreateShareResult: A function that shows the dialog and returns a promise
 *     resolving to the user's confirmation choice (true for confirm, false for cancel)
 */
export const useConfirmCreateShareDialog = () => {
  const resolverRef = useRef<ConfirmDialogResolver | null>(null);
  const [isShowing, setIsShowing] = useState(false);

  const onResult = useCallback((result: boolean) => {
    setIsShowing(false);
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  /**
   * Shows the confirmation dialog and returns a promise that resolves
   * when the user makes a choice.
   */
  async function getConfirmCreateShareResult() {
    return new Promise<boolean>((resolve) => {
      if (resolverRef.current) {
        throw new Error('Resolver already set');
      }
      resolverRef.current = resolve;
      setIsShowing(true);
    });
  }

  const confirmCreateShareDialog = (
    <ConfirmCreateShareDialog isShowing={isShowing} onConfirm={onResult} />
  );

  return {
    confirmCreateShareDialog,
    getConfirmCreateShareResult,
  };
};
